import type { RealProfile } from '../types/profiles';
import type { RankedItem } from '../types/communication';

export interface UserCreatedSubcategory {
  name: string;
  categoryName: string;
}

export interface RealCommunicationTotals {
  topCategories: RankedItem[];
  topSubcategories: RankedItem[];
  totalClicks: number;
  categoriesUsed: number;
  subcategoriesUsed: number;
  totalCategoriesAvailable: number;
  totalSubcategoriesAvailable: number;
  /** Categorias criadas pelo usuário além do catálogo padrão do app. */
  userCreatedCategories: string[];
  /** Subcategorias criadas pelo usuário (com a categoria a que pertencem). */
  userCreatedSubcategories: UserCreatedSubcategory[];
}

const FIXED_APP_CATEGORIES = 10;
const FIXED_APP_SUBCATEGORIES = 86;

function toRanked(map: Map<string, number>): RankedItem[] {
  return [...map.entries()]
    .map(([name, value]) => ({ name, value }))
    .sort((a, b) => b.value - a.value);
}

export function computeRealCommunicationTotals(profiles: RealProfile[]): RealCommunicationTotals {
  const categoryTotals = new Map<string, number>();
  const subcategoryTotals = new Map<string, number>();
  const userCreatedCategoryNames = new Set<string>();
  const userCreatedSubcategoryMap = new Map<string, UserCreatedSubcategory>(); // key = id

  for (const profile of profiles) {
    for (const cat of profile.categories) {
      if (cat.type === 'USER_CREATED') userCreatedCategoryNames.add(cat.name);
    }
    for (const sub of profile.subcategories) {
      userCreatedSubcategoryMap.set(sub.id, { name: sub.name, categoryName: sub.categoryName });
    }

    for (const click of profile.subcategoryClicks) {
      categoryTotals.set(
        click.categoryName,
        (categoryTotals.get(click.categoryName) ?? 0) + click.clickCount
      );
      subcategoryTotals.set(
        click.subcategoryName,
        (subcategoryTotals.get(click.subcategoryName) ?? 0) + click.clickCount
      );
    }
  }

  return {
    topCategories: toRanked(categoryTotals),
    topSubcategories: toRanked(subcategoryTotals),
    totalClicks: [...categoryTotals.values()].reduce((sum, v) => sum + v, 0),
    categoriesUsed: categoryTotals.size,
    subcategoriesUsed: subcategoryTotals.size,
    totalCategoriesAvailable: FIXED_APP_CATEGORIES + userCreatedCategoryNames.size,
    totalSubcategoriesAvailable: FIXED_APP_SUBCATEGORIES + userCreatedSubcategoryMap.size,
    userCreatedCategories: [...userCreatedCategoryNames].sort(),
    userCreatedSubcategories: [...userCreatedSubcategoryMap.values()].sort((a, b) =>
      a.categoryName.localeCompare(b.categoryName) || a.name.localeCompare(b.name)
    ),
  };
}
